'use client'

import React from 'react'
import { useFormContext } from 'react-hook-form'

import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/styles'

import { AppFormDescription } from './AppFormDescription'
import { AppFormLabel } from './AppFormLabel'

type Props = {
	readonly name: string
	readonly label?: React.ReactNode
	readonly endAdornment?: React.ReactNode
	readonly containerClassName?: string
	readonly fieldClassName?: string
	readonly description?: React.ReactNode
	readonly hideFormMessage?: boolean
	readonly iconLeft?: React.ReactNode
	readonly iconRight?: React.ReactNode
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>

export const AppField = React.forwardRef<HTMLInputElement, Props>(
	(
		{
			name,
			label,
			endAdornment,
			containerClassName,
			fieldClassName,
			description,
			hideFormMessage,
			iconLeft,
			iconRight,
			...nativeProps
		},
		ref,
	) => {
		const { control } = useFormContext()

		return (
			<FormField
				control={control}
				name={name}
				render={({ field, fieldState }) => (
					<FormItem className={cn(containerClassName)}>
						<AppFormLabel fieldState={fieldState} label={label} required={nativeProps.required} />

						<FormControl>
							<div className="relative flex items-stretch overflow-hidden w-full">
								{iconLeft && (
									<div className="absolute left-3 top-1/2 -translate-y-1/2 transform text-neutral-light-600">
										{iconLeft}
									</div>
								)}
								<Input
									ref={ref}
									variant="small"
									{...nativeProps}
									value={field.value ?? ''}
									onChange={(event) => {
										const value = event.target.value
										if (nativeProps.type === 'number') {
											field.onChange(Number.parseFloat(value))
										} else {
											field.onChange(value)
										}
									}}
									onBlur={field.onBlur}
									name={field.name}
									className={cn(
										'w-full',
										{
											'border-peach-500 text-peach-900': fieldState.invalid,
											border: !endAdornment,
										},
										iconLeft && 'pl-8',
										iconRight && 'pr-8',
										fieldClassName,
									)}
								/>
								{endAdornment && (
									<div
										className={cn('flex items-center border bg-gray-100 px-2', {
											'border-y-peach-500 border-r-peach-500 text-peach-900': fieldState.invalid,
											'border-gray-100': !fieldState.invalid,
										})}
									>
										{endAdornment}
									</div>
								)}
								{iconRight && (
									<div className="absolute right-3 top-1/2 -translate-y-1/2 transform text-neutral-light-600">
										{iconRight}
									</div>
								)}
							</div>
						</FormControl>

						<AppFormDescription description={description} />

						{!hideFormMessage && <FormMessage />}
					</FormItem>
				)}
			/>
		)
	},
)

AppField.displayName = 'AppField'
